import { Col, Form, Input, message, Modal, Row, Select } from "antd";
import { Rule } from "antd/lib/form";
import { projectApi } from "api/project.api";
import { roleApi } from "api/role.api";
import { staffApi } from "api/staff.api";
import { SingleImageUpload } from "components/Upload/SingleImageUpload";
import React, { useEffect, useImperativeHandle, useState } from "react";
import { userStore } from "store/userStore";
import { ModalStatus } from "types/modal";
import { Project } from "types/project";
import { Role } from "types/role";
import { Staff } from "types/staff";

const rules: Rule[] = [{ required: true }];
const { Option } = Select;

interface StaffForm extends Staff {
  roleId?: number;
  projectIds: number;
}

export interface StaffModal {
  handleCreate: () => void;
  handleUpdate: (Staff: Staff) => void;
}
interface StaffModalProps {
  onClose: () => void;
  onSubmitOk: () => void;
}

export const StaffModal = React.forwardRef(({ onClose, onSubmitOk }: StaffModalProps, ref) => {
  const [form] = Form.useForm<StaffForm>();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<ModalStatus>("create");
  const [selectedStaff, setSelectedStaff] = useState<Staff>();
  const [roles, setRoles] = useState<Role[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  useImperativeHandle<any, StaffModal>(
    ref,
    () => ({
      handleCreate() {
        form.resetFields();
        setVisible(true);
        setStatus("create");
        setSelectedStaff(undefined);
      },
      handleUpdate(Staff: Staff) {
        form.setFieldsValue({
          ...Staff,
          roleId: Staff.role.id,
          projectIds: Staff.projects.length > 0 ? Staff.projects[0].id : undefined,
        });
        setVisible(true);
        setStatus("update");
        setSelectedStaff(Staff);
      },
    }),
    [],
  );

  useEffect(() => {
    fetchRole();
    fetchProject();
  }, []);

  const fetchRole = async () => {
    const res = await roleApi.findAll({ page: 1, limit: 0 });
    setRoles(res.data);
  };

  const fetchProject = async () => {
    const res = await projectApi.findAll({ page: 1, limit: 0 });
    setProjects(res.data.projects);
  };

  const createData = async () => {
    const valid = await form.validateFields();
    const data = {
      staff: {
        name: form.getFieldValue("name"),
        username: form.getFieldValue("username"),
        email: form.getFieldValue("email"),
        password: form.getFieldValue("password"),
        avatar: form.getFieldValue("avatar"),
      },
      roleId: form.getFieldValue("roleId"),
      projectIds: [form.getFieldValue("projectIds")],
    };

    setLoading(true);
    try {
      await staffApi.create(data);
      message.success("Tạo mới thành công!");
      handleClose();
      onSubmitOk();
    } finally {
      setLoading(false);
    }
  };

  const updateData = async () => {
    const valid = await form.validateFields();
    const data = {
      staff: form.getFieldsValue(),
      roleId: form.getFieldValue("roleId"),
      projectIds: [form.getFieldValue("projectIds")],
    };
    setLoading(true);
    try {
      await staffApi.update(selectedStaff?.id || 0, data);
      message.success("Cập nhật thành công!");
      handleClose();
      onSubmitOk();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setVisible(false);
    setSelectedStaff(undefined);
    onClose();
  };

  // nếu là admin dự án thì đổ project cho nhân viên
  useEffect(() => {
    if (userStore.info.role?.name === "Admin dự án") {
      form.setFieldsValue({
        projectIds: userStore.info.projects?.[0]?.id,
      });
    }
  }, [form, userStore.info.role?.name]);

  return (
    <Modal
      onCancel={handleClose}
      open={visible}
      title={status == "create" ? "Tạo mới nhân viên" : "Cập nhật nhân viên"}
      style={{ top: 20 }}
      width={700}
      cancelText="Đóng"
      confirmLoading={loading}
      onOk={() => {
        status == "create" ? createData() : updateData();
      }}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Username" name="username" rules={rules}>
              <Input disabled={status == "update"} placeholder="" />
            </Form.Item>
          </Col>

          {status == "create" && (
            <Col span={12}>
              <Form.Item label="Mật khẩu" name="password" rules={rules}>
                <Input placeholder="" />
              </Form.Item>
            </Col>
          )}

          <Col span={12}>
            <Form.Item label="Họ tên" name="name" rules={rules}>
              <Input placeholder="" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Email" name="email">
              <Input placeholder="" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Quyền" name="roleId" rules={rules}>
              <Select style={{ width: "100%" }}>
                {roles.map((role) => (
                  <Option key={role.id} value={role.id}>
                    {role.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Dự án" name="projectIds">
              <Select style={{ width: "100%" }} allowClear>
                {projects?.map((project) => (
                  <Option key={project.id} value={project.id}>
                    {project.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item shouldUpdate={true}>
          {() => {
            return (
              <Form.Item label="Avatar" name="avatar">
                <SingleImageUpload
                  onUploadOk={(path: string) => {
                    console.log("onUploadOk:", path);
                    form.setFieldsValue({
                      avatar: path,
                    });
                  }}
                  imageUrl={form.getFieldValue("avatar")}
                  uploadUrl={import.meta.env.VITE_API_URL + "/v1/admin/staff/upload"}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
});
